'use client'

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form'
import { CustomerDetail } from '@/lib/api/customers'

const addressFormSchema = z.object({
	firstName: z.string().optional(),
	lastName: z.string().optional(),
	company: z.string().optional(),
	address1: z.string().optional(),
	address2: z.string().optional(),
	city: z.string().optional(),
	state: z.string().optional(),
	country: z.string().optional(),
	postal: z.string().optional(),
	phone: z.string().optional(),
	email: z.string().optional(),
})

export const customerEditFormSchema = z.object({
	email: z.string().email().optional().or(z.literal('')),
	firstName: z.string().optional(),
	lastName: z.string().optional(),
	billingAddress: addressFormSchema.optional(),
	shippingAddress: addressFormSchema.optional(),
})

export type CustomerEditFormData = z.infer<typeof customerEditFormSchema>

interface CustomerEditFormProps {
	customer: CustomerDetail
	onSubmit: (data: CustomerEditFormData) => void
	isLoading?: boolean
}

const buildDefaultValues = (customer: CustomerDetail): CustomerEditFormData => ({
	email: customer.email ?? '',
	firstName: customer.firstName ?? '',
	lastName: customer.lastName ?? '',
	billingAddress: {
		firstName: customer.billingAddress?.firstName ?? '',
		lastName: customer.billingAddress?.lastName ?? '',
		company: customer.billingAddress?.company ?? '',
		address1: customer.billingAddress?.address1 ?? '',
		address2: customer.billingAddress?.address2 ?? '',
		city: customer.billingAddress?.city ?? '',
		state: customer.billingAddress?.state ?? '',
		country: customer.billingAddress?.country ?? '',
		postal: customer.billingAddress?.postal ?? '',
		phone: customer.billingAddress?.phone ?? '',
		email: customer.billingAddress?.email ?? '',
	},
	shippingAddress: {
		firstName: customer.shippingAddress?.firstName ?? '',
		lastName: customer.shippingAddress?.lastName ?? '',
		company: customer.shippingAddress?.company ?? '',
		address1: customer.shippingAddress?.address1 ?? '',
		address2: customer.shippingAddress?.address2 ?? '',
		city: customer.shippingAddress?.city ?? '',
		state: customer.shippingAddress?.state ?? '',
		country: customer.shippingAddress?.country ?? '',
		postal: customer.shippingAddress?.postal ?? '',
		phone: customer.shippingAddress?.phone ?? '',
		email: customer.shippingAddress?.email ?? '',
	},
})

/**
 * Form for editing customer personal info and addresses
 */
export function CustomerEditForm({ customer, onSubmit, isLoading }: CustomerEditFormProps) {
	const { t } = useTranslation('customers')

	const form = useForm<CustomerEditFormData>({
		resolver: zodResolver(customerEditFormSchema),
		defaultValues: buildDefaultValues(customer),
	})

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
				<FormField
					name="email"
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('email')}</FormLabel>
							<FormControl>
								<Input {...field} type="email" />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<CustomerNameFields />
				<Separator />
				<AddressFields prefix="billingAddress" title={t('billingAddress')} />
				<Separator />
				<AddressFields prefix="shippingAddress" title={t('shippingAddress')} />
				<div className="flex justify-end pt-2">
					<Button type="submit" disabled={isLoading}>
						{isLoading ? t('common:saving') : t('common:save')}
					</Button>
				</div>
			</form>
		</Form>
	)
}

function CustomerNameFields() {
	const { t } = useTranslation('customers')

	return (
		<div className="grid grid-cols-2 gap-4">
			<FormField
				name="firstName"
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t('firstName')}</FormLabel>
						<FormControl>
							<Input {...field} />
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
			<FormField
				name="lastName"
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t('lastName')}</FormLabel>
						<FormControl>
							<Input {...field} />
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
		</div>
	)
}

interface AddressFieldsProps {
	prefix: 'billingAddress' | 'shippingAddress'
	title: string
}

function AddressFields({ prefix, title }: AddressFieldsProps) {
	const { t } = useTranslation('customers')

	return (
		<div className="space-y-4">
			<h3 className="text-sm font-semibold">{title}</h3>
			<div className="grid grid-cols-2 gap-4">
				<FormField
					name={`${prefix}.firstName`}
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('firstName')}</FormLabel>
							<FormControl>
								<Input {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					name={`${prefix}.lastName`}
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('lastName')}</FormLabel>
							<FormControl>
								<Input {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</div>
			<FormField
				name={`${prefix}.company`}
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t('company')}</FormLabel>
						<FormControl>
							<Input {...field} />
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
			<FormField
				name={`${prefix}.address1`}
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t('address1')}</FormLabel>
						<FormControl>
							<Input {...field} />
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
			<FormField
				name={`${prefix}.address2`}
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t('address2')}</FormLabel>
						<FormControl>
							<Input {...field} />
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
			<div className="grid grid-cols-2 gap-4">
				<FormField
					name={`${prefix}.city`}
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('city')}</FormLabel>
							<FormControl>
								<Input {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					name={`${prefix}.state`}
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('state')}</FormLabel>
							<FormControl>
								<Input {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</div>
			<div className="grid grid-cols-2 gap-4">
				<FormField
					name={`${prefix}.postal`}
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('postalCode')}</FormLabel>
							<FormControl>
								<Input {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					name={`${prefix}.country`}
					render={({ field }) => (
						<FormItem>
							<FormLabel>{t('country')}</FormLabel>
							<FormControl>
								<Input {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
			</div>
			<FormField
				name={`${prefix}.phone`}
				render={({ field }) => (
					<FormItem>
						<FormLabel>{t('phone')}</FormLabel>
						<FormControl>
							<Input {...field} type="tel" />
						</FormControl>
						<FormMessage />
					</FormItem>
				)}
			/>
		</div>
	)
}
